import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Star, Check, ShoppingCart, Download, ArrowLeft, Lock } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ScriptCard from "@/components/ScriptCard";

export default function ScriptDetails() {
  const { scriptId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [script, setScript] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const [owned, setOwned] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/scripts/${scriptId}`);
      setScript(data);
      setOwned(Boolean(data.content));
    } catch { toast.error("Could not load script"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scriptId]);

  const buy = async () => {
    if (!user) { navigate("/login"); return; }
    setBuying(true);
    try {
      await api.post("/orders/checkout", { script_id: scriptId });
      toast.success("Purchase complete. Enjoy your script.");
      await load();
    } catch { toast.error("Purchase failed"); }
    finally { setBuying(false); }
  };

  if (loading) return <div className="p-20 text-center text-white/60">Loading…</div>;
  if (!script) return <div className="p-20 text-center text-white/60">Not found</div>;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <Link to="/marketplace" className="text-white/60 text-sm inline-flex items-center gap-2 hover:text-white"><ArrowLeft className="h-4 w-4" /> Back to marketplace</Link>

      <div className="grid lg:grid-cols-3 gap-10 mt-8">
        <div className="lg:col-span-2">
          <div className="cw-card rounded-3xl overflow-hidden">
            <div className="relative h-72 lg:h-96">
              <img src={script.thumbnail} alt={script.title} className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F0F]/95 to-transparent" />
              <div className="absolute bottom-0 p-8">
                <div className="flex gap-2 mb-3">
                  <span className="text-xs px-3 py-1 rounded-full bg-white/10 border border-white/10">{script.platform}</span>
                  <span className="text-xs px-3 py-1 rounded-full bg-[#FF6B00]/20 border border-[#FF6B00]/40 text-[#FFB07A]">{script.duration}</span>
                  <span className="text-xs px-3 py-1 rounded-full bg-white/10 border border-white/10">{script.industry}</span>
                </div>
                <h1 className="font-display text-3xl lg:text-5xl leading-tight">{script.title}</h1>
              </div>
            </div>
            <div className="p-8 space-y-6">
              <div>
                <div className="cw-label mb-2">The Hook</div>
                <p className="text-xl font-display text-white/90 italic">"{script.hook}"</p>
              </div>
              <div>
                <div className="cw-label mb-2">Description</div>
                <p className="text-white/70 leading-relaxed">{script.description}</p>
              </div>

              <div>
                <div className="cw-label mb-2">Full Script</div>
                {script.content ? (
                  <pre data-testid="script-content" className="bg-black/40 border border-white/10 rounded-xl p-5 whitespace-pre-wrap text-white/85 text-sm leading-relaxed font-body">{script.content}</pre>
                ) : (
                  <div className="bg-black/40 border border-white/10 rounded-xl p-8 text-center">
                    <Lock className="h-6 w-6 text-[#FF6B00] mx-auto mb-3" />
                    <div className="font-display text-lg">Preview locked</div>
                    <p className="text-white/50 text-sm mt-1">Purchase this script to unlock the full content and downloadable version.</p>
                  </div>
                )}
              </div>

              <div>
                <div className="cw-label mb-3">What's included</div>
                <ul className="grid sm:grid-cols-2 gap-2">
                  {["Full annotated script","Shot list & on-screen text","3 CTA variations","Full commercial rights"].map((f) => (
                    <li key={f} className="flex items-center gap-2 text-white/80 text-sm"><Check className="h-4 w-4 text-[#FF6B00]" /> {f}</li>
                  ))}
                </ul>
              </div>

              {script.reviews?.length > 0 && (
                <div>
                  <div className="cw-label mb-3">Reviews</div>
                  <div className="space-y-3">
                    {script.reviews.map((r) => (
                      <div key={r.review_id} className="cw-card rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-display text-sm">{r.user_name}</span>
                          <div className="flex text-[#FF6B00]">{[...Array(r.rating)].map((_,i)=><Star key={i} className="h-3 w-3 fill-[#FF6B00]" />)}</div>
                        </div>
                        <p className="text-white/70 text-sm">{r.comment}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <aside className="lg:sticky lg:top-24 h-fit">
          <div className="cw-card rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-3">
              <Star className="h-4 w-4 fill-[#FF6B00] text-[#FF6B00]" />
              <span className="text-sm">{script.rating || "New"} <span className="text-white/40">({script.reviews_count || 0} reviews)</span></span>
            </div>
            <div className="font-display text-5xl">₹{script.price}</div>
            <div className="text-white/40 text-sm mt-1">One-time • Full commercial rights</div>
            {owned ? (
              <button data-testid="download-script" className="cw-btn-primary w-full mt-6 inline-flex items-center justify-center gap-2" onClick={() => {
                const blob = new Blob([script.content], { type: "text/plain" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a"); a.href = url; a.download = `${script.title}.txt`; a.click();
                URL.revokeObjectURL(url);
              }}>
                <Download className="h-4 w-4" /> Download script
              </button>
            ) : (
              <button data-testid="buy-script" disabled={buying} onClick={buy} className="cw-btn-primary w-full mt-6 inline-flex items-center justify-center gap-2">
                <ShoppingCart className="h-4 w-4" /> {buying ? "Processing…" : "Buy Now"}
              </button>
            )}
            <div className="mt-6 pt-6 border-t border-white/10">
              <div className="cw-label mb-2">Written by</div>
              <div className="text-sm">{script.writer_name}</div>
            </div>
          </div>
        </aside>
      </div>

      {script.similar?.length > 0 && (
        <section className="mt-16">
          <h2 className="font-display text-3xl mb-6">Similar scripts</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {script.similar.map((s) => <ScriptCard key={s.script_id} script={s} />)}
          </div>
        </section>
      )}
    </div>
  );
}
